import { Typography, List, Box, Grid, Stack } from '@mui/material';
import React, {
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import { AppPrimaryButton } from 'shared/components/AppComponents';
import ListItemReviewedNotes from './ListItemReviewedNotes';
import { useLocation, useNavigate } from 'react-router-dom';

import { ReviewData } from '../../solicitor-viewer/components/Footer';
import { ManagePacketService } from 'services/ManagePacketService';
import { Toast, Utils } from 'shared/helpers';
import { createDateAsUTC } from 'shared/helpers/AgeOfDate';
import { useSelector, useDispatch } from 'react-redux';
import { BroadcastActions } from 'state/broadcast/Action';
import {
    useEvent,
    usePageLeave,
    useTimeoutFn,
    useUpdateEffect,
} from 'react-use';
import { CarryAppActions } from 'state/carriesapplication/Action';
import { SocketActions } from 'state/socket/Action';
import { LOCATION_CHANGE } from 'redux-first-history';
import { Action } from 'history';
import { BrowserMeta } from 'BrowserHistory';
import { AxiosError } from 'axios';
import { useLeavePrompt } from 'shared/helpers/UseExitPrompt';
import isPointerInside from 'shared/helpers/isPointerInside';

const DetailReviewed: React.FC = () => {
    const navigation = useNavigate();
    const dispatch = useDispatch();
    const location = useLocation();
    const browserProps = useContext(BrowserMeta);
    const [isActiveBackNavigation, setActiveBackNavigation] =
        useState<boolean>(false);
    const defaultState = location.state as {
        reviewData: ReviewData[];
        documentData: reviewedData;
        primary?: boolean;
    };
    useEffect(() => {
        browserProps?.setBackState(defaultState);
    }, []);
    const broadcastProgression = useSelector(
        (state: any) => state.broadcastState.broadcastProgression
    );

    const broadcastPageLeave = useSelector(
        (state: any) => state.broadcastState.broadcastPageLeave
    );

    const profileState = useSelector((state: any) => state.profileState);
    const socketState = useSelector((state: any) => state.socketState);
    const socketEvent = React.useMemo(
        () => socketState.socketData.eventName,
        [socketState.socketData.eventName]
    );
    const router = useSelector((state: any) => state.router);

    const currentIdx = useMemo(
        () =>
            window.history.state &&
            typeof window.history.state.idx !== 'undefined'
                ? window.history.state.idx
                : null,
        []
    );
    const currentLength = useMemo(
        () =>
            window.history.length &&
            typeof window.history.length !== 'undefined'
                ? window.history.length
                : null,
        []
    );
    const isPrimaryRoute = useMemo(
        () =>
            router &&
            router.action === 'REPLACE' &&
            typeof currentIdx === 'number' &&
            currentIdx === 0,
        [currentIdx, router]
    );

    const isHistoricRouter = useMemo(
        () =>
            router &&
            router.action === 'PUSH' &&
            typeof currentLength === 'number' &&
            (currentLength === 2 || currentLength === 1) &&
            typeof currentIdx === 'number' &&
            currentIdx === 0,
        [currentIdx, currentLength, router]
    );
    const isHistoricPopRouter = useMemo(
        () =>
            router &&
            router.action === 'POP' &&
            typeof currentLength === 'number' &&
            currentLength === 1 &&
            typeof currentIdx === 'number' &&
            currentIdx === 0,
        [currentIdx, currentLength, router]
    );

    const broadcastArray = useSelector(
        (state: any) => state.broadcastState.broadcastData
    );

    const broadcastReviewedArray = useSelector(
        (state: any) => state.broadcastState.broadcastReviewedData
    );
    const tabId = useSelector((state: any) => state.carryState.tabId);
    const persistTabId = useSelector((state: any) => state.persistState.tabId);

    const [isSubmitting, setSubmitting] = useState<boolean>(false);
    const [isFreeze, setFreezing] = useState<boolean>(false);

    /** @constant {reviewedData} - document data constant from location react dom */
    const documentData = useMemo(
        () =>
            defaultState && defaultState.documentData
                ? {
                      ...defaultState.documentData,
                      _id: defaultState.documentData.actions.uuid,
                  }
                : {
                      _id: '',
                      surName: null,
                      uniqueRef: null,
                      reviewRequired: false,
                      statusMessage: null,
                      slaDate: null,
                      clientId: '',
                      SLADate: new Date(),
                      clientName: '',
                      loanNumber: '',
                      bin: '',
                      packetType: '',
                      operatorId: '',
                      completedDate: new Date(),
                      escalatedDate: new Date(),
                      uploadedDate: new Date(),
                      recordNo: '',
                      packetStatus: '',
                      actions: null,
                      documents: [],
                      file: null,
                      reportFile: null,
                  },
        [defaultState]
    );
    const packetId = useMemo(
        () => documentData.actions?.uuid ?? '',
        [documentData]
    );
    // eslint-disable-next-line consistent-return
    const isActiveBroadcast = useMemo(
        () =>
            Array.isArray(broadcastArray) &&
            broadcastArray.length > 0 &&
            broadcastArray.filter(
                (t) =>
                    t.pathname === '/single-reviewed' &&
                    t.state &&
                    t.state.documentData &&
                    t.state.documentData.actions &&
                    t.state.documentData.actions.uuid === packetId
            ).length > 0,
        [broadcastArray, packetId]
    );

    const [countingTabActive, setCountTabActive] = useState<number>(0);
    const [isGoingBackBySocket, setGoingBackBySocket] =
        React.useState<boolean>(false);

    const [isGoingBackBySocketSamePacket, setGoingBackBySocketSamePacket] =
        React.useState<boolean>(false);
    const [isGoingBackByDulplication, setGoingBackByDuplication] =
        React.useState<boolean>(false);
    const isTabActive = useSelector(
        (state: any) => state.carryState.visibilityState
    );

    const handleVisibilityChange = useCallback(() => {
        dispatch(
            CarryAppActions.SetCarryVisibilityState(
                document.visibilityState === 'visible'
            )
        );
    }, []);

    useEvent('visibilitychange', handleVisibilityChange);
    useUpdateEffect(() => {
        if (socketEvent !== null) {
            if (socketEvent === 'operatorAction') {
                if (
                    // eslint-disable-next-line no-underscore-dangle
                    profileState.userInfo._id ===
                        socketState.socketData.operatorId &&
                    isActiveBroadcast === false &&
                    isTabActive === false
                ) {
                    setGoingBackBySocket(true);
                    setGoingBackByDuplication(true);
                }
            }
        }
    }, [socketEvent]);

    useUpdateEffect(() => {
        if (socketEvent !== null) {
            if (socketEvent === 'operatorAction') {
                if (
                    // eslint-disable-next-line no-underscore-dangle
                    profileState.userInfo._id ===
                        socketState.socketData.operatorId &&
                    isTabActive === false &&
                    location.pathname === '/single-reviewed'
                ) {
                    if (countingTabActive === 0) {
                        dispatch(
                            BroadcastActions.SetBroadcastProgression(true)
                        );
                        setGoingBackBySocketSamePacket(
                            socketState.socketData.packetId === packetId
                        );
                        setGoingBackBySocket(
                            socketState.socketData.packetId !== packetId
                        );
                    } else {
                        setCountTabActive((prev) => {
                            if (prev > 0) {
                                return prev - 1;
                            }
                            return prev;
                        });
                    }
                }
            }
            dispatch(SocketActions.ResetSocketEvent({}));
        }
    }, [socketEvent]);

    /** @constant {ReviewData[]} - review data constant from location react dom */
    const reviewData = useMemo(
        () =>
            defaultState && defaultState.reviewData
                ? defaultState.reviewData
                : [],
        [defaultState]
    );

    const isReplaceRoute = useMemo(
        () =>
            defaultState && defaultState.primary ? defaultState.primary : false,
        [defaultState]
    );

    const [isAlreadyHaveSubmit, setAlreadyHaveSubmit] =
        useState<boolean>(false);
    useUpdateEffect(() => {
        if (isSubmitting) {
            setAlreadyHaveSubmit(true);
        }
    }, [isSubmitting]);

    const handleFreezingOnSubmit = useCallback(() => {
        setAlreadyHaveSubmit(false);
        setFreezing(false);
    }, []);
    const [isReady, cancel, reset] = useTimeoutFn(handleFreezingOnSubmit, 1000);
    useUpdateEffect(() => {
        if (isTabActive === false && isAlreadyHaveSubmit) {
            if (broadcastPageLeave) {
                if (isReady() === false) {
                    cancel();
                }
            } else {
                reset();
            }
        }
    }, [isTabActive, broadcastPageLeave]);
    useEffect(() => {
        if (isReady() === false) {
            cancel();
        }
        return () => {
            setFreezing(false);
        };
    }, []);
    useUpdateEffect(() => {
        if (
            isActiveBroadcast === false &&
            isTabActive === true &&
            isSubmitting === false &&
            isFreeze === false &&
            broadcastProgression
        ) {
            const replaceRoute = isReplaceRoute ? -1 : -2;
            if (isPrimaryRoute) {
                navigation('/manage-packets', { replace: true });
            } else {
                navigation(replaceRoute);
            }
            dispatch(BroadcastActions.SetBroadcastProgression(false));
        }
    }, [isActiveBroadcast, isTabActive, broadcastProgression]);

    // const recordLocations = useSelector(
    //     (state: any) => state.persistState.recordLocations
    // );

    const isPageLeave = useSelector(
        (state: any) => state.carryState.isPageLeave
    );

    const isRecordLocale = useMemo(
        () =>
            broadcastReviewedArray &&
            broadcastReviewedArray &&
            broadcastReviewedArray.filter(
                (e: any) =>
                    e &&
                    e.pathname === '/single-reviewed' &&
                    e.state !== null &&
                    e.state.documentData &&
                    e.state.documentData.actions &&
                    e.state.documentData.actions.uuid &&
                    e.state.documentData.actions.uuid === packetId
            ).length > 1 &&
            isPageLeave,
        [broadcastReviewedArray, isPageLeave, packetId]
    );

    useEffect(() => {
        if (isRecordLocale) {
            ManagePacketService.patchOperatorAction({
                open: true,
                packetId,
            });
        }
    }, []);

    // NOTE: this similar to componentWillUnmount()
    React.useEffect(() => {
        if (
            defaultState &&
            defaultState.documentData &&
            typeof defaultState.documentData.isOpen !== 'undefined' &&
            defaultState.documentData.isOpen === false
        ) {
            ManagePacketService.patchOperatorAction({
                open: true,
                packetId,
            })
                .then(() => {
                    // Utils.handleSuccessResponse(response);
                    dispatch({
                        type: LOCATION_CHANGE,
                        payload: {
                            location: {
                                ...location,
                                state: {
                                    ...location.state,
                                    documentData:
                                        location.state &&
                                        location.state.documentData
                                            ? {
                                                  ...location.state
                                                      .documentData,
                                                  isOpen: true,
                                              }
                                            : {
                                                  isOpen: true,
                                              },
                                },
                            },
                            action: Action.Replace,
                        },
                    });
                })
                .catch(Utils.handleErrorResponse);
        }
    }, []);
    useUpdateEffect(() => {
        if (
            (isGoingBackBySocket || isGoingBackBySocketSamePacket) &&
            isFreeze === false
        ) {
            const replaceRoute = isReplaceRoute ? -1 : -2;
            if (isGoingBackByDulplication || isGoingBackBySocketSamePacket) {
                dispatch({
                    type: LOCATION_CHANGE,
                    payload: {
                        location: {
                            ...location,
                            state: {
                                ...location.state,
                                documentData:
                                    location.state &&
                                    location.state.documentData
                                        ? {
                                              ...location.state.documentData,
                                              isOpen: false,
                                          }
                                        : {
                                              isOpen: false,
                                          },
                            },
                        },
                        action: Action.Replace,
                    },
                });
            }
            if (isPrimaryRoute) {
                navigation('/manage-packets', {
                    replace: true,
                });
            } else {
                navigation(replaceRoute);
            }
        }
    }, [isGoingBackBySocket, isGoingBackBySocketSamePacket]);

    const sortedSerializeReviewData = useMemo(
        () =>
            reviewData
                ? Array.from(reviewData).sort((a, b) => a.sequence - b.sequence)
                : [],
        [reviewData]
    );

    // const [openDetailPanel, setOpenDetailPanel] = React.useState(false);
    // const handleChange = React.useCallback(
    //     (event: React.ChangeEvent<HTMLInputElement>) => {
    //         setOpenDetailPanel(event.target.checked);
    //     },
    //     []
    // );

    /**
     * Get Promise based rework send request .
     * @param {ReviewData} review - review data from document viewer.
     * @param {DocumentWithReason} documents - document data from document viewer.
     * @return {Promise<any>} Promise to fetch.
     */
    const getReworkFetchUpdate = React.useCallback(
        (review: ReviewData, documents: DocumentWithReason[]): Promise<any> =>
            new Promise((resolve, reject) => {
                // eslint-disable-next-line @typescript-eslint/no-unused-vars
                // const { placeholder: b, ..._document } = document;
                // const documents = [
                //     {
                //         ..._document,
                //     },
                // ];
                const expirityOnlyDate = new Date(
                    review.expiryDate ?? new Date()
                );
                expirityOnlyDate.setHours(0, 0, 0, 0);

                ManagePacketService.sendToRework({
                    // stepId: review._id,
                    documents,
                    clientId: documentData.clientId,
                    packetId: review.packetId,
                    solicitor: {
                        solicitorId: review.solicitorData?.solicitorId ?? '',
                        contactId: review.solicitorData?.contactId ?? '',
                        name: review.solicitorData?.name ?? '',
                    },
                    expiryDate: review.expiryDate
                        ? createDateAsUTC(expirityOnlyDate).toISOString()
                        : '',
                })
                    .then(resolve)
                    .catch(reject);
            }),
        [documentData.clientId]
    );
    /**
     * Get Promise based review send request .
     * @param {ReviewData} review - review data from document viewer.
     * @return {Promise<any>} Promise to fetch.
     */
    const sendReviewPacket = React.useCallback(
        (reviews: ReviewData[]): Promise<any> =>
            new Promise((resolve, reject) => {
                ManagePacketService.reviewPacket({
                    packetId: documentData.actions?.uuid ?? '',
                    reviewedSteps: Array.from(reviews).map((el) => {
                        const {
                            // eslint-disable-next-line @typescript-eslint/no-unused-vars
                            placeholder: a,
                            // eslint-disable-next-line @typescript-eslint/no-unused-vars
                            // documentId: a2,
                            // eslint-disable-next-line @typescript-eslint/no-unused-vars
                            file: a3,
                            sortedVertices: sortedVerticesCompareWith,
                            // eslint-disable-next-line @typescript-eslint/no-unused-vars
                            value: v1,
                            ...compareWith
                        } = el.compareWith;

                        const {
                            // eslint-disable-next-line @typescript-eslint/no-unused-vars
                            placeholder: b,
                            // eslint-disable-next-line @typescript-eslint/no-unused-vars
                            // documentId: b2,
                            // eslint-disable-next-line @typescript-eslint/no-unused-vars
                            file: b3,
                            sortedVertices: sortedVerticesCompareTo,
                            // eslint-disable-next-line @typescript-eslint/no-unused-vars
                            value: v2,
                            ...compareTo
                        } = el.compareTo;

                        return {
                            _id: el.actions.uuid,
                            step: el.step,
                            compareWith:
                                typeof compareWith === 'object' &&
                                Object.keys(compareWith).filter(
                                    (o) =>
                                        o !== 'sortedVertices' &&
                                        o !== 'coverLetterDetails'
                                ).length > 0
                                    ? {
                                          ...compareWith,
                                          documentId:
                                              typeof compareWith.documentId ===
                                              'undefined'
                                                  ? null
                                                  : compareWith.documentId,
                                          sortedVertices:
                                              compareWith.coverLetterDetails ===
                                              null
                                                  ? Array.from(
                                                        sortedVerticesCompareWith ??
                                                            []
                                                    ).map((o) => {
                                                        const {
                                                            // eslint-disable-next-line @typescript-eslint/no-unused-vars
                                                            isActive,
                                                            // eslint-disable-next-line @typescript-eslint/no-unused-vars
                                                            isDrawn: userMarked,
                                                            // eslint-disable-next-line @typescript-eslint/no-unused-vars
                                                            ratio,
                                                            ...ox
                                                        } = o;
                                                        return {
                                                            ...ox,
                                                            userMarked,
                                                        };
                                                    })
                                                  : [],
                                          coverLetterDetails:
                                              compareWith.coverLetterDetails !==
                                              null
                                                  ? {
                                                        ...compareWith.coverLetterDetails,
                                                        sortedVertices:
                                                            Array.from(
                                                                compareWith
                                                                    .coverLetterDetails
                                                                    .sortedVertices ??
                                                                    []
                                                            )
                                                                .map((o) => {
                                                                    const {
                                                                        // eslint-disable-next-line @typescript-eslint/no-unused-vars
                                                                        isActive,
                                                                        // eslint-disable-next-line @typescript-eslint/no-unused-vars
                                                                        isDrawn:
                                                                            userMarked,
                                                                        // eslint-disable-next-line @typescript-eslint/no-unused-vars
                                                                        ratio,
                                                                        ...ox
                                                                    } = o;
                                                                    return {
                                                                        ...ox,
                                                                        userMarked,
                                                                    };
                                                                })
                                                                .filter(
                                                                    (x) =>
                                                                        x.userMarked ===
                                                                        true
                                                                ),
                                                    }
                                                  : compareWith.coverLetterDetails,
                                      }
                                    : undefined,
                            compareTo:
                                typeof compareTo === 'object' &&
                                Object.keys(compareTo).filter(
                                    (o) =>
                                        o !== 'sortedVertices' &&
                                        o !== 'coverLetterDetails'
                                ).length > 0
                                    ? {
                                          ...compareTo,
                                          documentId:
                                              typeof compareTo.documentId ===
                                              'undefined'
                                                  ? null
                                                  : compareTo.documentId,
                                          sortedVertices:
                                              compareWith.coverLetterDetails ===
                                              null
                                                  ? Array.from(
                                                        sortedVerticesCompareTo ??
                                                            []
                                                    ).map((o) => {
                                                        const {
                                                            // eslint-disable-next-line @typescript-eslint/no-unused-vars
                                                            isActive,
                                                            // eslint-disable-next-line @typescript-eslint/no-unused-vars
                                                            isDrawn: userMarked,
                                                            // eslint-disable-next-line @typescript-eslint/no-unused-vars
                                                            ratio,
                                                            ...ox
                                                        } = o;
                                                        return {
                                                            ...ox,
                                                            userMarked,
                                                        };
                                                    })
                                                  : [],
                                          coverLetterDetails:
                                              compareTo.coverLetterDetails !==
                                              null
                                                  ? {
                                                        ...compareTo.coverLetterDetails,
                                                        sortedVertices:
                                                            Array.from(
                                                                compareTo
                                                                    .coverLetterDetails
                                                                    .sortedVertices ??
                                                                    []
                                                            )
                                                                .map((o) => {
                                                                    const {
                                                                        // eslint-disable-next-line @typescript-eslint/no-unused-vars
                                                                        isActive,
                                                                        // eslint-disable-next-line @typescript-eslint/no-unused-vars
                                                                        isDrawn:
                                                                            userMarked,
                                                                        // eslint-disable-next-line @typescript-eslint/no-unused-vars
                                                                        ratio,
                                                                        ...ox
                                                                    } = o;
                                                                    return {
                                                                        ...ox,
                                                                        userMarked,
                                                                    };
                                                                })
                                                                .filter(
                                                                    (x) =>
                                                                        x.userMarked ===
                                                                        true
                                                                ),
                                                    }
                                                  : compareTo.coverLetterDetails,
                                      }
                                    : undefined,
                            result: el.result,
                            message:
                                el.result === 'Escalated'
                                    ? el.escalatedReason
                                    : undefined,
                        };
                    }),
                    // moveToCMP9: openDetailPanel,
                })
                    .then(resolve)
                    .catch(reject);
            }),

        [documentData.actions?.uuid]
    );

    /**
     * handle back button to navigate previous permalink .
     */
    const handleBack = React.useCallback(() => {
        setActiveBackNavigation(true);
        if (isPrimaryRoute || isHistoricRouter || isHistoricPopRouter) {
            navigation('/reviewer-viewer', {
                replace: true,
                state: {
                    ...documentData,
                    reviewData,
                },
            });
        } else {
            navigation(-1);
        }
    }, [
        documentData,
        isHistoricPopRouter,
        isHistoricRouter,
        isPrimaryRoute,
        navigation,
        reviewData,
    ]);

    /**
     * handle submit button.
     */
    const handleSubmit = React.useCallback(() => {
        const saveBroadcastArray = broadcastArray;
        const replaceRoute = isReplaceRoute ? -1 : -2;
        dispatch(
            BroadcastActions.SetRemoveBroadcastReduxMessage({
                ...location,
                tabId,
                parentTabId: persistTabId,
            })
        );
        setFreezing(true);
        setSubmitting(true);
        const SendToReworkRightDocument = Array.from(
            reviewData.filter(
                (o) => o.result === 'Send For Rework' && o.isSecondDocument
            )
        ).map((element) => ({
            step: element.step,
            // eslint-disable-next-line no-underscore-dangle
            stepId: element._id,
            documentId: element.compareTo.documentId
                ? element.compareTo.documentId
                : null,
            category: element.compareTo.category,
            file: element.compareTo.file,
            isMissingDocument:
                element.compareTo.documentId === null ||
                typeof element.compareTo.documentId === 'undefined',
            // placeholder: element.compareTo.placeholder,
            statusMessage: element.secondReason ?? '',
            predefinedMessage: element.secondReasonPreDefined ?? '',
        }));

        const SendToReworkLeftDocument = Array.from(
            reviewData.filter(
                (o) => o.result === 'Send For Rework' && o.isFirstDocument
            )
        ).map((element) => ({
            step: element.step,
            // eslint-disable-next-line no-underscore-dangle
            stepId: element._id,
            documentId: element.compareWith.documentId
                ? element.compareWith.documentId
                : null,
            category: element.compareWith.category,
            file: element.compareWith.file,
            isMissingDocument:
                element.compareWith.documentId === null ||
                typeof element.compareWith.documentId === 'undefined',
            // placeholder: element.compareWith.placeholder,
            statusMessage: element.firstReason ?? '',
            predefinedMessage: element.firstReasonPreDefined ?? '',
        }));
        const findSentToReworkData = reviewData.find(
            (o) => o.result === 'Send For Rework'
        );

        const SentToReworkDocument: reviewToSent[] = [
            ...SendToReworkRightDocument,
            ...SendToReworkLeftDocument,
        ].sort((a, b) => b.step - a.step);

        const UniqueSentToReworkDocument = Array.from(
            SentToReworkDocument
        ).filter(
            (e, i) =>
                SentToReworkDocument.findIndex(
                    (a) =>
                        (e.isMissingDocument === true &&
                            a.isMissingDocument === true &&
                            e.category === a.category) ||
                        (e.isMissingDocument === false &&
                            a.isMissingDocument === false &&
                            a.documentId === e.documentId)
                ) === i
        );

        // let moveToCM9 = null;
        // if (openDetailPanel) {
        //     moveToCM9 = ManagePacketService.moveToCM9({
        //
        //         // stepId: review._id,
        //         cm9Number: formikRef.current?.values.CM9code ?? '',
        //
        //         packetId: documentData._id,
        //     });
        // }

        if (findSentToReworkData) {
            Promise.all([
                sendReviewPacket(reviewData),
                getReworkFetchUpdate(
                    findSentToReworkData,
                    UniqueSentToReworkDocument
                ),
            ])
                .then((response) => {
                    if (
                        response &&
                        Array.isArray(response) &&
                        response.length > 0
                    ) {
                        Utils.handleSuccessResponse(response[0]);
                    }
                    dispatch(BroadcastActions.SetBroadcastProgression(true));
                    // setSubmitting(false);
                    if (
                        isPrimaryRoute ||
                        isHistoricRouter ||
                        isHistoricPopRouter
                    ) {
                        navigation('/manage-packets', { replace: true });
                    } else {
                        navigation(replaceRoute);
                    }
                })
                .catch((error) => {
                    dispatch(
                        BroadcastActions.SetCloneBroadcastReduxMessage(
                            saveBroadcastArray
                        )
                    );
                    dispatch(BroadcastActions.SetBroadcastProgression(false));
                    setSubmitting(false);
                    if (
                        error !== null &&
                        error instanceof AxiosError &&
                        error.code === 'ERR_NETWORK'
                    ) {
                        Toast.showErrorMessage(
                            'No internet connection, Please try again!'
                        );
                    } else {
                        Utils.handleErrorResponse(error);
                    }
                });
        } else {
            Promise.all([sendReviewPacket(reviewData)])
                .then((response) => {
                    if (
                        response &&
                        Array.isArray(response) &&
                        response.length > 0
                    ) {
                        Utils.handleSuccessResponse(response[0]);
                    }
                    dispatch(BroadcastActions.SetBroadcastProgression(true));
                    // setSubmitting(false);
                    if (
                        isPrimaryRoute ||
                        isHistoricRouter ||
                        isHistoricPopRouter
                    ) {
                        navigation('/manage-packets', { replace: true });
                    } else {
                        navigation(replaceRoute);
                    }
                })
                .catch((error) => {
                    dispatch(
                        BroadcastActions.SetCloneBroadcastReduxMessage(
                            saveBroadcastArray
                        )
                    );
                    dispatch(BroadcastActions.SetBroadcastProgression(false));
                    setSubmitting(false);
                    if (
                        error !== null &&
                        error instanceof AxiosError &&
                        error.code === 'ERR_NETWORK'
                    ) {
                        Toast.showErrorMessage(
                            'No internet connection, Please try again!'
                        );
                    } else {
                        Utils.handleErrorResponse(error);
                    }
                });
        }
    }, [
        broadcastArray,
        dispatch,
        getReworkFetchUpdate,
        isHistoricPopRouter,
        isHistoricRouter,
        isPrimaryRoute,
        isReplaceRoute,
        location,
        navigation,
        persistTabId,
        reviewData,
        sendReviewPacket,
        tabId,
    ]);

    const innerMainRef = useRef<HTMLDivElement | null>(null);
    const callbackForPromptAlert = React.useCallback(() => {
        const { current: containerRef } = innerMainRef;
        ManagePacketService.patchOperatorAction({
            open: false,
            packetId,
        })
            .then(() => {
                containerRef?.classList.add('patch');
                // Utils.handleSuccessResponse(response);
                navigation('/single-reviewed', {
                    replace: true,
                    state: {
                        ...location.state,
                        documentData:
                            location.state && location.state.documentData
                                ? {
                                      ...location.state.documentData,
                                      isOpen: true,
                                  }
                                : {
                                      isOpen: true,
                                  },
                    },
                });
                // window.history.state.isOpen = false;
                //               window.history.replaceState({ ba: false }, '');
            })
            .catch(Utils.handleErrorResponse);
    }, [location.state, navigation, packetId]);

    const [, showLeavePrompt] = useLeavePrompt(false, callbackForPromptAlert);
    const [isLeavePage, setLeavePage] = React.useState<boolean>(false);
    usePageLeave(() => {
        setLeavePage(true);
    });
    const handlePatchingOperatorLockWithTimeout = React.useCallback(() => {
        ManagePacketService.patchOperatorAction({
            open: true,
            packetId: packetId ?? '',
        })
            .then(() => {
                dispatch({
                    type: LOCATION_CHANGE,
                    payload: {
                        location: {
                            ...location,
                            state: {
                                ...location.state,
                                documentData:
                                    location.state &&
                                    location.state.documentData
                                        ? {
                                              ...location.state.documentData,
                                              isOpen: true,
                                          }
                                        : {
                                              isOpen: true,
                                          },
                            },
                        },
                    },
                });
                // const { current: modalRef } = innerModalRef;
                // modalRef?.classList.add('patch');
                // Utils.handleSuccessResponse(response);
                // navigate('/not-expected-packet', {
                //     replace: true,
                //     state: {
                //         ...defaultState,
                //         isOpen: false,
                //     },
                // });
                // window.history.state.isOpen = false;
                //               window.history.replaceState({ ba: false }, '');
            })
            .catch(Utils.handleErrorResponse);
    }, [dispatch, location, packetId]);
    const [, cancelOperatorLock, resetOperatorLock] = useTimeoutFn(
        handlePatchingOperatorLockWithTimeout,
        500
    );
    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    const handleMouseEnter = React.useCallback(
        (event: any) => {
            const { current: containerRef } = innerMainRef;

            if (isPointerInside(containerRef, event)) {
                if (containerRef?.classList.contains('patch')) {
                    containerRef.classList.remove('patch');
                    resetOperatorLock();
                }
                setLeavePage(false);
            }
        },
        [resetOperatorLock]
    );
    useUpdateEffect(() => {
        const { current: containerRef } = innerMainRef;
        if (containerRef) {
            if (isLeavePage) {
                containerRef.addEventListener('mouseenter', handleMouseEnter);
            } else {
                containerRef.removeEventListener(
                    'mouseenter',
                    handleMouseEnter
                );
            }
        }
    }, [isLeavePage]);

    useEffect(() => {
        cancelOperatorLock();
        showLeavePrompt(true);
        return () => {
            cancelOperatorLock();
            showLeavePrompt(false);
        };
    }, []);

    return (
        <Box className="container-reviewed" ref={innerMainRef}>
            <Typography variant="h2">Good Job!</Typography>
            <Typography variant="h3">
                Cross check reviewed successfully
            </Typography>
            <List className="list-reviewed-notes">
                {sortedSerializeReviewData.map((el, index) => (
                    <ListItemReviewedNotes
                        key={`${index} - ${el.actions.uuid}`}
                        TitleNotes={el.description}
                        ToRework={el.result === 'Send For Rework'}
                        NotApplicable={el.result === 'N/A'}
                        DocMissing={el.result === 'Document Missing'}
                        PaperDoc={el.result === 'Paper Document'}
                        Escalated={el.result === 'Escalated'}
                        Correct={el.result === 'Correct'}
                        Handsign={el.result === 'Hand Signed'}
                        Notverifiedsign={el.result === 'Sign Not Verified'}
                    />
                ))}
            </List>
            <Grid container spacing={0} className="reviewed-panel-cm">
                {/* <Grid container item spacing={0} className="container-panel">
                    <Grid item xs={4} sm={1}>
                        <SwitchComp
                            defaultChecked={openDetailPanel}
                            onChange={handleChange}
                        />
                    </Grid>
                    <Grid item xs={8} sm={5}>
                        <Typography variant="h6">
                            Move this Packet to CM9
                        </Typography>
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <FormControl id="action-panel-for-cm">
                            <Grid container spacing={2}>
                                <Grid item xs={12} sm={6} md={6}>
                                    <AppPrimaryButton
                                        title="Download documents"
                                        className="btn-small download"
                                        onClick={() =>
                                            downloadDocs(
                                                defaultState.documentData.file
                                            )
                                        }
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6} md={6}>
                                    <AppPrimaryButton
                                        title="Download Report"
                                        className="btn-small download"
                                        onClick={() =>
                                            downloadDocs(
                                                defaultState.documentData
                                                    .reportFile
                                            )
                                        }
                                    />
                                </Grid>
                                 <Grid item xs={12} sm={2} md={4}>
                                            <AppFormikTextField
                                                name="CM9code"
                                                placeholder="CM9 Code"
                                            />
                                        </Grid>
                            </Grid>
                        </FormControl>
                    </Grid>
                </Grid> */}
            </Grid>
            <Box className="action-submit-reviewed">
                <Stack className="stackButton" direction="row">
                    <AppPrimaryButton
                        title="Back"
                        className="btn-small"
                        onClick={handleBack}
                        disabled={isActiveBackNavigation || isSubmitting}
                    />
                    <AppPrimaryButton
                        title="Submit"
                        className="btn-small processing-btn"
                        isLoading={isSubmitting}
                        loadingSize={22}
                        disabled={
                            isSubmitting ||
                            isActiveBackNavigation ||
                            isActiveBroadcast === false
                        }
                        loadingText="PROCESSING"
                        onClick={handleSubmit}
                    />
                </Stack>
            </Box>
        </Box>
    );
};

export default DetailReviewed;
